// Zvonek v hlavičce (F4 4.3 „notifikace řidiči“, kontrakt Notifikace): ikona s počtem nepřečtených (`neprectene`
// z GET /ja/notifikace, po přihlášení a pak každou minutu), po kliknutí panel (jako nabídka účtu) se seznamem —
// nadpis, podtitul, čas („před 5 min“, jinak datum s časem), tečka podle závažnosti; klik na položku ji označí
// přečtenou (POST /ja/notifikace/precteno { ids }) a otevře `odkaz` (nebo kartu entity; věci z fronty K vyřízení — zakázka
// ke kontrole, hlášení, návrh z pošty, událost ISŘ — rovnou řádek ve frontě, 05/18); „Označit vše jako přečtené“
// ({ vse: true }). Vede‑li odkaz rovnou na formulář (`odkaz_akce`), položka nese tlačítko s názvem akce („Odběr
// proveden“ — 27. 10. 2026). Prázdný stav „Nic nového“. Přístupnost: počet v aria-live, panel role="dialog", Esc zavře.
import type { Ja } from '@ecoser/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { SEKCE_K_VYRIZENI } from '../api/k-vyrizeni';
import { duvodChyby } from '../api/klient';
import {
  cilNotifikace,
  KLIC_NOTIFIKACE,
  type Notifikace as NotifikaceZaznam,
  type NotifikaceSeznam,
  obnovitNotifikace,
  oznacitPrectene,
  popisKdy,
  useNotifikace,
  ZAVAZNOSTI,
} from '../api/notifikace';
import { TlacitkoSDuvodem } from '../komponenty/TlacitkoSDuvodem';
import { useToast } from '../komponenty/Toast';
import { Ikona } from './ikony';
import './notifikace.css';

/** „1 nepřečtená“, „3 nepřečtené“, „5 nepřečtených“ — pro popisek tlačítka a čtečku. */
export function popisNeprectenych(pocet: number): string {
  if (pocet === 0) return 'Žádné nepřečtené notifikace';
  const tvar =
    pocet === 1 ? 'nepřečtená' : pocet >= 2 && pocet <= 4 ? 'nepřečtené' : 'nepřečtených';
  return `${pocet} ${tvar}`;
}

export function Notifikace({ ja }: { ja: Ja }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const seznam = useNotifikace(Boolean(ja.zamestnanec));
  const [otevreny, setOtevreny] = useState(false);
  const [pracuji, setPracuji] = useState(false);
  const tlacitko = useRef<HTMLButtonElement>(null);
  const bylOtevreny = useRef(false);
  const neprectene = seznam.data?.neprectene ?? 0;
  const polozky = seznam.data?.polozky ?? [];
  // Věci z fronty K vyřízení (zakázka ke kontrole, hlášení, návrh z pošty, událost ISŘ) vedou na řádek ve frontě.
  const mameFrontu = (ja.sekce ?? []).includes(SEKCE_K_VYRIZENI);

  useEffect(() => {
    if (!otevreny) return undefined;
    const naKlavesu = (udalost: KeyboardEvent) => {
      if (udalost.key === 'Escape') setOtevreny(false);
    };
    document.addEventListener('keydown', naKlavesu);
    return () => document.removeEventListener('keydown', naKlavesu);
  }, [otevreny]);

  // Po zavření vrátit fokus na zvonek.
  useEffect(() => {
    if (bylOtevreny.current && !otevreny) tlacitko.current?.focus();
    bylOtevreny.current = otevreny;
  }, [otevreny]);

  /** Optimisticky upraví cache (počet a stav řádků) a pak nechá seznam načíst znovu. */
  const zapsatPrecteni = (ids: string[] | 'vse', novyPocet: number) => {
    queryClient.setQueryData<NotifikaceSeznam>(KLIC_NOTIFIKACE, (puvodni) =>
      puvodni
        ? {
            ...puvodni,
            neprectene: novyPocet,
            polozky: puvodni.polozky.map((n) =>
              n.stav === 'nova' && (ids === 'vse' || ids.includes(n.id))
                ? { ...n, stav: 'prectena' }
                : n,
            ),
          }
        : puvodni,
    );
    obnovitNotifikace(queryClient);
  };

  const otevritPolozku = async (n: NotifikaceZaznam) => {
    const cil = cilNotifikace(n, { fronta: mameFrontu, sekce: ja.sekce ?? [] });
    if (n.stav === 'nova') {
      try {
        const odpoved = await oznacitPrectene({ ids: [n.id] });
        zapsatPrecteni([n.id], odpoved.neprectene);
      } catch (e) {
        toast.ukazat(duvodChyby(e));
      }
    }
    setOtevreny(false);
    if (cil) navigate(cil);
  };

  const oznacitVse = async () => {
    setPracuji(true);
    try {
      const odpoved = await oznacitPrectene({ vse: true });
      zapsatPrecteni('vse', odpoved.neprectene);
      toast.ukazat(
        odpoved.oznaceno === 0
          ? 'Nic nového k přečtení.'
          : `Označeno jako přečtené: ${odpoved.oznaceno}.`,
      );
    } catch (e) {
      toast.ukazat(duvodChyby(e));
    } finally {
      setPracuji(false);
    }
  };

  return (
    <div className="zvonek">
      <button
        ref={tlacitko}
        type="button"
        className="zvonek__tlacitko"
        aria-haspopup="dialog"
        aria-expanded={otevreny}
        aria-controls={otevreny ? 'zvonek-panel' : undefined}
        aria-label={`Notifikace — ${popisNeprectenych(neprectene)}`}
        title={`Notifikace — ${popisNeprectenych(neprectene)}`}
        data-testid="zvonek"
        onClick={() => setOtevreny((o) => !o)}
      >
        <Ikona id="bell" velikost={17} />
        {neprectene > 0 && (
          <span className="zvonek__pocet" data-testid="zvonek-pocet">
            {neprectene > 99 ? '99+' : neprectene}
          </span>
        )}
      </button>
      {/* Počet pro čtečku obrazovky: změna se ohlásí, aniž by musel uživatel na zvonek. */}
      <span className="jen-pro-ctecky" aria-live="polite" aria-atomic="true">
        {seznam.data ? popisNeprectenych(neprectene) : ''}
      </span>
      {otevreny && (
        <>
          <button
            type="button"
            className="prekryv zvonek__podklad"
            aria-label="Zavřít notifikace"
            tabIndex={-1}
            onClick={() => setOtevreny(false)}
          />
          <div
            id="zvonek-panel"
            className="zvonek__panel"
            role="dialog"
            aria-label="Notifikace"
            data-testid="zvonek-panel"
          >
            <div className="zvonek__hlava">
              <h2 className="zvonek__titul">Notifikace</h2>
              <TlacitkoSDuvodem
                varianta="textove"
                vRadku
                disabled={pracuji || neprectene === 0}
                duvod={neprectene === 0 ? 'Vše je přečtené.' : undefined}
                onClick={() => void oznacitVse()}
              >
                Označit vše jako přečtené
              </TlacitkoSDuvodem>
            </div>
            {seznam.isPending && <p className="zvonek__prazdne">Načítám…</p>}
            {seznam.isError && (
              <p className="zvonek__prazdne" role="alert">
                Notifikace se nepodařilo načíst. {duvodChyby(seznam.error)}
              </p>
            )}
            {seznam.data && polozky.length === 0 && <p className="zvonek__prazdne">Nic nového</p>}
            {polozky.length > 0 && (
              <ul className="zvonek__seznam">
                {polozky.map((n) => {
                  const cil = cilNotifikace(n, { fronta: mameFrontu, sekce: ja.sekce ?? [] });
                  return (
                    <li key={n.id}>
                      <button
                        type="button"
                        className={`zvonek__polozka${n.stav === 'nova' ? ' zvonek__polozka--nova' : ''}`}
                        data-testid="notifikace-polozka"
                        data-stav={n.stav}
                        title={cil ? (n.odkaz_akce ?? 'Otevřít') : 'Označit jako přečtené'}
                        onClick={() => void otevritPolozku(n)}
                      >
                        <span
                          className={`zvonek__tecka zvonek__tecka--${n.zavaznost}`}
                          role="img"
                          aria-label={ZAVAZNOSTI[n.zavaznost] ?? n.zavaznost}
                        />
                        <span className="zvonek__obsah">
                          <span className="zvonek__nadpis">{n.nadpis}</span>
                          {n.text && <span className="zvonek__text">{n.text}</span>}
                          <span className="zvonek__kdy">{popisKdy(n.kdy)}</span>
                          {/* Tlačítko přímo v upozornění (27. 10. 2026): klepnutím se otevře formulář akce. */}
                          {cil && n.odkaz_akce && (
                            <span
                              className="tlacitko tlacitko--primarni tlacitko--radek zvonek__akce"
                              data-testid="notifikace-akce"
                            >
                              {n.odkaz_akce}
                            </span>
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
